<template>
  <div>
    <div v-if="authType === 'apiKey'">
      <!-- CRM logo -->
      <div class="flex justify-center p-3">
        <img src="@/assets/icons/hubspot.png" class="w-56 mb-6 mt-5" />
      </div>

      <!-- Description -->
      <div
        class="m-4 mt-2"
        v-html="$t('integrations.crm.hubspot.guideApiKey')"
      />

      <!-- Docs Hubspot api link -->
      <div class="block mt-5">
        {{ $t("integrations.crm.hubspot.docs.part1") }}
        <a
          class="text-blue-800"
          href="https://knowledge.hubspot.com/integrations/how-do-i-get-my-hubspot-api-key"
        >
          {{ $t("integrations.crm.hubspot.docs.link") }}
        </a>
        {{ $t("integrations.crm.hubspot.docs.part2") }}
      </div>

      <div
        class="h-full w-full flex flex-col justify-center items-center py-10"
      >
        <!-- api key input -->
        <r-text-field
          v-model="apiKey"
          refs="hubspotApiKey"
          id="crm-apikey-input"
          :label="$t('integrations.crm.apiKey')"
          class="w-full py-2"
        />
        <label
          v-show="isApiKeyError"
          class="w-full text-red-600 text-xs transition-all text-left"
        >
          {{ $t(`errors.validateHubspotAPIKey`) }}
        </label>
      </div>

      <div class="flex flex-row w-full justify-end mb-3">
        <r-button
          id="crm-apikey-save-button"
          @click="apiKeyWithHubspot"
          :loading="loading"
          :disabled="isApiKeyFormDisabled"
        >
          {{ $t("buttons.save") }}
        </r-button>
        <!-- Close button - visible only om Mobile view -->
        <div class="md:hidden ml-3">
          <r-button color="black" text @click="$emit('close')">
            {{ $t("buttons.close") }}
          </r-button>
        </div>
      </div>
    </div>
    <div v-else class="mb-10 p-5">
      <!-- crm logo -->
      <div class="flex justify-center">
        <img src="@/assets/icons/hubspot.png" class="w-56 mb-6 mt-2" />
      </div>

      <!-- Description -->
      <div class="mt-3" v-html="$t('integrations.crm.hubspot.guideOauth')" />

      <!-- sign in oauth with hubspot -->
      <div class="flex justify-center">
        <r-button
          id="sign-in-with-hubspot-button"
          @click="oauthWithHubspot"
          :loading="loading"
          text
          class="flex item-center justify-center md:w-64 w-full my-8"
        >
          <!-- icon -->
          <img src="@/assets/icons/hubspot-mini.png" class="w-5 mr-2" />

          <!-- content -->
          <div class="content-center justify-center">
            {{ $t("buttons.continueWithHusbpot") }}
          </div>
        </r-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import hubspotAPI from "@/api/hubspot"

export default {
  // local state
  data() {
    return {
      apiKey: "",
      loading: false,
      isApiKeyError: false
    }
  },

  // define props
  props: {
    mode: {
      type: String,
      required: true
    },
    authType: {
      type: String,
      required: true
    }
  },

  // computed
  computed: {
    ...mapGetters({
      // org attribute
      org: "orgs/getDoc",
      jwt: "auth/getIdToken"
    }),

    /**
     * Determine if api key form disabled
     */
    isApiKeyFormDisabled() {
      return !this.apiKey
    }
  },

  // watch property
  watch: {
    org: {
      immediate: true,
      handler() {
        if (this.org?.hubspot?.api_key && this.mode === "edit") {
          this.apiKey = this.org?.hubspot?.api_key
        }
      }
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // methods
  methods: {
    /**
     * OAuth with hubspot
     */
    async oauthWithHubspot() {
      this.loading = true

      try {
        // init api
        const api = hubspotAPI("NONE_API", this.jwt)

        // get auth url
        const { auth_url } = await api.notify("auth_url")

        location.replace(auth_url)
      } catch (e) {
        console.error(e)
        this.notifyError({
          title: this.$t("errors.unableToLoginWithHubspot"),
          message: e.message
        })
      }

      this.loading = false
      this.closeDialog()
    },

    /**
     * API Key auth with hubspot
     */
    async apiKeyWithHubspot() {
      this.loading = true

      try {
        // check if api key is valid
        const isValid = await this.validateHubspotKey()

        // update api key in fs if valid, else reise an error
        if (isValid) {
          await this.updateHubspotKey()
          this.closeDialog()
        } else {
          this.isApiKeyError = true
        }
      } catch (e) {
        console.error(e)
        this.notifyError({
          title: this.$t("errors.unableToLoginWithHubspot"),
          message: e.message
        })
      }

      this.loading = false
    },

    /**
     * Validate husbpot api key
     */
    async validateHubspotKey() {
      const api = hubspotAPI(this.apiKey, this.jwt)
      try {
        // make health check
        await api.notify("validate_apikey", {
          apikey: this.apiKey
        })

        // if not got an exception - returns true
        return true
      } catch (e) {
        console.error(e)
        return false
      }
    },

    /**
     * Update hubspot api key
     */
    updateHubspotKey() {
      return this.$store.dispatch("orgs/update", {
        id: this.org.id,
        data: {
          hubspot: {
            api_key: this.apiKey
          }
        }
      })
    },

    /**
     * Close dialog
     */
    closeDialog() {
      this.$emit("close")
    }
  }
}
</script>
